import { bindable, ICustomAttributeViewModel, INode, ISignaler, resolve } from "aurelia";
import { Signals } from "@aurelia/i18n";
import { createPopper, Instance as PopperInstance, Placement } from "@popperjs/core";

export class TooltipCustomAttribute implements ICustomAttributeViewModel {
  @bindable value: string;
  @bindable placement: Placement;
  @bindable showOnTruncation: boolean | string | undefined;

  popperRef: PopperInstance;
  tooltip: HTMLElement;
  isTruncated: boolean = false;

  removeSignalListener: () => void;
  private signaler: ISignaler = resolve(ISignaler);
  private element = resolve(INode) as HTMLElement;

  constructor() {
    this.tooltip = this.createTooltipElement();
  }

  private createTooltipElement(): HTMLElement {
    // Create tooltip container with all necessary classes
    const tooltip = document.createElement("div");
    tooltip.className = [
      "absolute",
      "box-border",
      "invisible",
      "opacity-0",
      "p-2",
      "font-normal",
      "leading-6",
      "text-left",
      "text-sm",
      "break-words",
      "z-[1080]",
      "transition-opacity",
      "duration-300",
      "ease-linear",
      "[&.show]:visible",
      "[&.show]:opacity-100",

      // Arrow positioning
      "[&[data-popper-placement^='top']_#tooltip-arrow]:-bottom-[2px]",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow]:-top-[1px]",
      "[&[data-popper-placement^='right']_#tooltip-arrow]:-left-[8px]",
      "[&[data-popper-placement^='right']_#tooltip-arrow]:-translate-y-[3px]",
      "[&[data-popper-placement^='left']_#tooltip-arrow]:-right-[8px]",
      "[&[data-popper-placement^='left']_#tooltip-arrow]:-translate-y-[3px]",

      // Arrow triangle
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:absolute",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:content-['']",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:-top-px",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:border-l-[6px]",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:border-r-[6px]",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:border-t-[6px]",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:border-l-transparent",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:border-r-transparent",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:border-t-ob-instrument-dynamic",
      "[&[data-popper-placement^='top']_#tooltip-arrow::before]:border-b-0",

      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:absolute",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:content-['']",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:-bottom-px",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:border-l-[6px]",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:border-r-[6px]",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:border-b-[6px]",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:border-l-transparent",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:border-r-transparent",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:border-b-ob-instrument-dynamic",
      "[&[data-popper-placement^='bottom']_#tooltip-arrow::before]:border-t-0",

      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:absolute",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:content-['']",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:-right-px",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:border-t-[6px]",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:border-b-[6px]",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:border-r-[6px]",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:border-t-transparent",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:border-b-transparent",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:border-r-ob-instrument-dynamic",
      "[&[data-popper-placement^='right']_#tooltip-arrow::before]:border-l-0",

      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:absolute",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:content-['']",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:-left-px",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:border-t-[6px]",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:border-b-[6px]",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:border-l-[6px]",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:border-t-transparent",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:border-b-transparent",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:border-l-ob-instrument-dynamic",
      "[&[data-popper-placement^='left']_#tooltip-arrow::before]:border-r-0",
    ].join(" ");

    // Create content div
    const content = document.createElement("div");
    content.className =
      "max-w-40 px-1 py-1.5 text-center text-ob-text-neutral bg-ob-background shadow-sm shadow-ob-instrument-dynamic rounded";

    // Create arrow div
    const arrow = document.createElement("div");
    arrow.id = "tooltip-arrow";
    arrow.className = "block w-3 h-1.5 drop-shadow-lg";
    arrow.setAttribute("data-popper-arrow", "");

    tooltip.appendChild(content);
    tooltip.appendChild(arrow);

    return tooltip;
  }

  private get shouldCheckTruncation(): boolean {
    if (typeof this.showOnTruncation === "string") {
      return this.showOnTruncation.toLowerCase() === "true";
    }
    return this.showOnTruncation === true;
  }

  private get isTruncationDisabled(): boolean {
    if (typeof this.showOnTruncation === "string") {
      return this.showOnTruncation.toLowerCase() === "false";
    }
    return this.showOnTruncation === false;
  }

  valueChanged() {
    const contentElement = this.tooltip.querySelector<HTMLDivElement>("div:first-child");
    if (contentElement) {
      contentElement.textContent = this.value;
    }
  }

  bound() {
    this.valueChanged();
    this.element.parentElement.appendChild(this.tooltip);
  }

  attached() {
    this.element.addEventListener("mouseenter", this.showTooltip);
    this.element.addEventListener("mouseleave", this.hideTooltip);

    if (this.shouldCheckTruncation) {
      this.checkForTruncation();
      const resizeObserver = new ResizeObserver(() => this.checkForTruncation());
      resizeObserver.observe(this.element);
    }

    const subscriber = {
      handleChange: () => this.popperRef?.forceUpdate(),
    };

    this.signaler.addSignalListener(Signals.I18N_SIGNAL, subscriber);
    this.removeSignalListener = () =>
      this.signaler.removeSignalListener(Signals.I18N_SIGNAL, subscriber);
  }

  checkForTruncation() {
    this.isTruncated = this.element.scrollHeight > this.element.clientHeight;
  }

  detaching() {
    this.removeSignalListener();
    this.popperRef?.destroy();
    this.element.removeEventListener("mouseenter", this.showTooltip);
    this.element.removeEventListener("mouseleave", this.hideTooltip);
    this.tooltip?.remove();
  }

  showTooltip = () => {
    let shouldShow = false;

    if (this.showOnTruncation === undefined) {
      shouldShow = true;
    } else if (this.shouldCheckTruncation) {
      shouldShow = this.isTruncated;
    } else if (this.isTruncationDisabled) {
      shouldShow = false;
    }

    if (shouldShow) {
      if (!this.popperRef) {
        this.popperRef = createPopper(this.element, this.tooltip, {
          placement: this.placement || "top",
          modifiers: [
            {
              name: "flip",
              options: {
                fallbackPlacements: ["bottom", "top", "right", "left"],
                altBoundary: true,
                padding: 8,
              },
            },
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ],
        });
      }
      
      if (this.value === '') {
        return;
      }   
      
      this.popperRef.forceUpdate();
      this.tooltip.classList.add("show");
    }
  };

  hideTooltip = () => {
    this.tooltip.classList.remove("show");
  };
}
